import {
  isMembershipBacked,
  isSystemPipelineId,
  mergeSystemPipelinesAsPipelines,
} from 'app/components/action-center/systemPipelines'
import { graphql } from 'app/generated/gql'
import type { Pipeline } from 'app/generated/graphql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { isCompanionPipeline } from 'lib/companionPipelines'
import { logError } from 'lib/logging'

const PIPELINES_FILTER_QUERY = graphql(`
  query PipelinesForFilter {
    pipelines {
      id
      name
      orgId
      workspaceId
      stages {
        name
        color
        order
      }
    }
  }
`)

type PipelineStageLite = { name: string; color: string; order: number }
type PipelineLite = {
  id: string
  name: string
  orgId?: string
  workspaceId?: string
  stages?: PipelineStageLite[] | null
}

export type FilterFetchResult = {
  hits: { id: string; name: string; group?: string }[]
  total: number
  page: number
}

/**
 * Whether the system pipelines belong in this dropdown.
 *
 * A workspace without the Action Center cannot open, work or delete Hygiene
 * Recall and Unscheduled treatment (every `/action-center/*` URL is bounced home
 * by `FeatureRoute`), so offering them as something to filter contacts by offers
 * a dead end. The caller decides: `filterableColumns` reads `ACTION_CENTER_FLAG`
 * the same way it already reads `hygieneEnabled`.
 *
 * It arrives at construction because the filter framework calls a fetcher with
 * its own params, leaving nowhere to pass it at call time.
 */
export type SystemPipelineVisibility = { includeSystemPipelines: boolean }

async function loadPipelines({ includeSystemPipelines }: SystemPipelineVisibility): Promise<PipelineLite[]> {
  try {
    const result = await gqlQuery<{ pipelines: Pipeline[] }>(PIPELINES_FILTER_QUERY, {})
    const fromApi = result.data?.pipelines ?? []
    // System pipelines are frontend constants with no server record (see
    // systemPipelines.ts), so they are merged in rather than fetched.
    // The off branch drops reserved ids for the same reason the merge does: a
    // record carrying one is not the constant these filters are named after.
    const all = includeSystemPipelines
      ? mergeSystemPipelinesAsPipelines(fromApi)
      : fromApi.filter((pipeline) => !isSystemPipelineId(pipeline.id))
    // Active Leads is excluded: a contact's place in it is `leadStatus`, not a
    // `pipelines` membership, so filtering on it would always match zero contacts.
    return all
      .filter(isMembershipBacked)
      .filter((pipeline) => !isCompanionPipeline(pipeline))
      .map((pipeline) => ({
        id: pipeline.id,
        name: pipeline.name,
        stages: (pipeline.stages ?? []).map((stage) => ({ name: stage.name, color: stage.color, order: stage.order })),
      }))
  } catch (err) {
    logError('pipelineFilterFetchers: failed to load pipelines', err)
    return []
  }
}

/**
 * The async combo resolves an already-applied filter value by re-querying with each
 * selected id double-quoted and OR-joined; ids must resolve by id, not name, or the
 * selection silently reverts to "All" (TRK-5262). Stage names may themselves contain
 * `"`, which makes tokenizing that transport ambiguous, so instead of parsing it we
 * return the known ids that appear wrapped as `"<id>"`. Returns [] for a plain
 * (unquoted) user search.
 */
export function selectQuotedIds(query: string | undefined, knownIds: string[]): string[] {
  if (!query || !query.includes(`"`)) return []
  return knownIds.filter((id) => query.includes(`"${id}"`))
}

/**
 * The filter framework passes a selected pipeline value into the Stage fetcher as a string.
 * A single selection arrives as `"pl_sales"`; multiple selections arrive OR-wrapped as
 * `"(pl_sales OR pl_recall)"` (see AsyncMultiSelectFilter contextual handling). Normalize both.
 */
export function parseContextualPipelineIds(raw?: string | string[]): string[] {
  if (!raw) return []
  const text = Array.isArray(raw) ? raw.join(` OR `) : raw
  return text
    .replace(/^\(|\)$/g, ``)
    .split(/\s+OR\s+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Builds the `pipelines.id` filter's option fetcher for one workspace's toggle state. */
export function pipelinesFilterFetcher(visibility: SystemPipelineVisibility) {
  return async (params: { query: string; [key: string]: string | undefined }): Promise<FilterFetchResult> => {
    const pipelines = await loadPipelines(visibility)
    const resolved = selectQuotedIds(
      params.query,
      pipelines.map((p) => p.id),
    )
    if (resolved.length) {
      const wanted = new Set(resolved)
      const hits = pipelines.filter((p) => wanted.has(p.id)).map((p) => ({ id: p.id, name: p.name }))
      return { hits, total: hits.length, page: 0 }
    }
    const q = params.query?.toLowerCase() ?? ``
    const hits = pipelines
      .filter((p) => !q || p.name.toLowerCase().includes(q))
      .map((p) => ({ id: p.id, name: p.name }))
    return { hits, total: hits.length, page: 0 }
  }
}

/** Builds the `pipelines.key` (stage) filter's option fetcher for one workspace's toggle state. */
export function pipelineStagesFilterFetcher(visibility: SystemPipelineVisibility) {
  return async (params: {
    query: string
    pipelineId?: string
    [key: string]: string | undefined
  }): Promise<FilterFetchResult> => {
    const ids = parseContextualPipelineIds(params.pipelineId)
    const pipelines = await loadPipelines(visibility)
    const targets = ids.length ? pipelines.filter((p) => ids.includes(p.id)) : pipelines
    const allHits = targets.flatMap((p) =>
      (p.stages ?? [])
        .slice()
        .sort((a, b) => a.order - b.order)
        // Composite token `<pipelineId>::<stageName>`, the exact value the backend indexes
        // on `pipelines.key`. Stage names may contain `::` but pipeline ids never do, so
        // both sides must split on the FIRST `::` only.
        .map((s) => ({ id: `${p.id}::${s.name}`, name: s.name, group: p.name })),
    )
    const resolved = selectQuotedIds(
      params.query,
      allHits.map((h) => h.id),
    )
    if (resolved.length) {
      const wanted = new Set(resolved)
      const hits = allHits.filter((h) => wanted.has(h.id))
      return { hits, total: hits.length, page: 0 }
    }
    const q = params.query?.toLowerCase() ?? ``
    const hits = allHits.filter((h) => !q || h.name.toLowerCase().includes(q) || h.group.toLowerCase().includes(q))
    return { hits, total: hits.length, page: 0 }
  }
}
